import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';

/**
 * Workflow definition DTO.
 */
export interface WorkflowDefinitionDto {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  version: string;
  workflowType: string;
  patternType: string;
  supportsCheckpointing: boolean;
  supportsStreaming: boolean;
  tenantId: string;
  projectId?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Workflow detail DTO (includes full JSON).
 */
export interface WorkflowDetailDto extends WorkflowDefinitionDto {
  workflowJson: string;
  startAgentDefinitionId?: string;
  metadataJson?: string;
}

/**
 * Workflow execution DTO.
 */
export interface WorkflowExecutionDto {
  id: string;
  workflowDefinitionId: string;
  workflowName: string;
  status: string;
  currentNodeId?: string;
  correlationId?: string;
  startedAt?: string;
  completedAt?: string;
  pausedAt?: string;
  errorMessage?: string;
  tenantId: string;
  projectId?: string;
  createdAt: string;
}

/**
 * Workflow checkpoint DTO.
 */
export interface WorkflowCheckpointDto {
  id: string;
  executionId: string;
  nodeId: string;
  sequenceNumber: number;
  stateJson: string;
  createdAt: string;
}

/**
 * Workflow execution summary DTO returned by the executions endpoints.
 * Status is a string name (Pending, Running, Paused, Completed, Failed, Cancelled).
 */
export interface WorkflowExecutionSummaryDto {
  id: string;
  workflowDefinitionId: string;
  workflowName: string;
  status: string;
  currentNodeId?: string;
  correlationId?: string;
  startedAt?: string;
  completedAt?: string;
  pausedAt?: string;
  errorMessage?: string;
  /** Populated only by the single-execution endpoint. */
  stateJson?: string;
  createdAt: string;
}

/**
 * Workflow graph validation response.
 */
export interface ValidateWorkflowGraphResponse {
  isValid: boolean;
  errors: string[];
}

/**
 * Create workflow request.
 * workflowType/patternType may be omitted; the backend defaults to Custom/None.
 */
export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  workflowJson: string;
  workflowType?: string;
  patternType?: string;
  startAgentDefinitionId?: string;
  supportsCheckpointing: boolean;
  supportsStreaming: boolean;
  version: string;
  metadataJson?: string;
}

/**
 * Update workflow request.
 */
export interface UpdateWorkflowRequest {
  name: string;
  description?: string;
  workflowJson: string;
  isActive: boolean;
  version: string;
  metadataJson?: string;
}

/**
 * Execute workflow request.
 */
export interface ExecuteWorkflowRequest {
  parameters?: Record<string, unknown>;
  correlationId?: string;
}

/**
 * Execute workflow response.
 */
export interface ExecuteWorkflowResponse {
  executionId: string;
  workflowDefinitionId: string;
  status: string;
  /** Aggregated multi-agent parent session for this run (when the graph has agent nodes). */
  parentSessionId?: string | null;
}

/**
 * Resume workflow request.
 */
export interface ResumeWorkflowRequest {
  triggerData?: unknown;
}

/**
 * Workflow filters for queries.
 */
export interface WorkflowFilters {
  pageNumber?: number;
  pageSize?: number;
  isActive?: boolean;
  searchTerm?: string;
}

/**
 * Workflow service for handling workflow operations.
 */
export class WorkflowService extends BaseService {
  /**
   * Gets a paginated list of workflow definitions.
   */
  async getWorkflows(filters?: WorkflowFilters): Promise<PagedResult<WorkflowDefinitionDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);

    const url = `/workflows${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<WorkflowDefinitionDto>>(url);
  }

  /**
   * Gets a workflow definition by ID.
   */
  async getWorkflow(id: string): Promise<WorkflowDetailDto> {
    return this.get<WorkflowDetailDto>(`/workflows/${id}`);
  }

  /**
   * Creates a new workflow definition.
   */
  async createWorkflow(data: CreateWorkflowRequest): Promise<WorkflowDetailDto> {
    return this.post<WorkflowDetailDto>('/workflows', data);
  }

  /**
   * Updates an existing workflow definition.
   */
  async updateWorkflow(id: string, data: UpdateWorkflowRequest): Promise<WorkflowDetailDto> {
    return this.put<WorkflowDetailDto>(`/workflows/${id}`, data);
  }

  /**
   * Deletes a workflow definition.
   */
  async deleteWorkflow(id: string): Promise<void> {
    return this.delete<void>(`/workflows/${id}`);
  }

  /**
   * Executes a workflow.
   */
  async executeWorkflow(id: string, request?: ExecuteWorkflowRequest): Promise<ExecuteWorkflowResponse> {
    return this.post<ExecuteWorkflowResponse>(`/workflows/${id}/execute`, request);
  }

  /**
   * Resumes a paused workflow execution.
   */
  async resumeWorkflow(executionId: string, request?: ResumeWorkflowRequest): Promise<WorkflowExecutionDto> {
    return this.post<WorkflowExecutionDto>(`/workflows/executions/${executionId}/resume`, request);
  }

  /**
   * Gets checkpoints for a workflow execution.
   */
  async getCheckpoints(executionId: string): Promise<WorkflowCheckpointDto[]> {
    return this.get<WorkflowCheckpointDto[]>(`/workflows/executions/${executionId}/checkpoints`);
  }

  /**
   * Gets recent executions for a workflow definition (most recent first).
   */
  async getExecutions(workflowId: string, limit = 20): Promise<WorkflowExecutionSummaryDto[]> {
    return this.get<WorkflowExecutionSummaryDto[]>(`/workflows/${workflowId}/executions?limit=${limit}`);
  }

  /**
   * Gets a single workflow execution, including its current state JSON.
   */
  async getExecution(executionId: string): Promise<WorkflowExecutionSummaryDto> {
    return this.get<WorkflowExecutionSummaryDto>(`/workflows/executions/${executionId}`);
  }

  /**
   * Validates a workflow graph JSON without saving it.
   */
  async validateGraph(workflowJson: string): Promise<ValidateWorkflowGraphResponse> {
    return this.post<ValidateWorkflowGraphResponse>('/workflows/validate', { workflowJson });
  }
}

export const workflowService = new WorkflowService();
